import { completeSignup } from "@/utils/signup";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { cn } from "@/lib/utils";
import { checkCreateProspectRateLimit } from "@/utils/createProspectRateLimiter";
import { trackCompleteRegistration } from "@/utils/facebook-pixel-events";
import logger from "@/utils/logger";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";

// reCAPTCHA site keys
const RECAPTCHA_SCORE_KEY =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
  "6LdewqYrAAAAACyovzEoE3tUtmtNi4ZgLTnqzTnn";
// reCAPTCHA v2 Checkbox key for fallback when v3 score is low
const RECAPTCHA_CHECKBOX_KEY =
  import.meta.env.VITE_RECAPTCHA_CHECKBOX_KEY ||
  "6Lexl10sAAAAAMFs_DawLbU7xGg9ogJcgHy3dWDH";

// Utility functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function ErrorMessage({ message, type = "field" }) {
  return (
    <div className="flex items-center gap-2">
      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 " />
      <p
        className={
          type === "field" ? "text-xs text-red-600" : "text-sm text-red-600"
        }
      >
        {message}
      </p>
    </div>
  );
}

// Shared content component to avoid duplication
function AuthContent({
  email,
  setEmail,
  loading,
  setLoading,
  onClose,
  initialPlan = "pro",
}) {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(initialPlan); // Use initialPlan prop
  const [errors, setErrors] = useState({});

  // reCAPTCHA ref for invisible reCAPTCHA (Score)
  const recaptchaScoreRef = useRef(null);
  // reCAPTCHA ref for visible reCAPTCHA (Checkbox/Challenge)
  const recaptchaChallengeRef = useRef(null);

  // State to control whether to show the visible challenge
  const [showChallenge, setShowChallenge] = useState(false);

  const handleContactSubmit = async () => {
    // Validate form
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!selectedPlan) {
      newErrors.plan = "Please select a plan";
      setErrors(newErrors);
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check client-side rate limit
    const rateLimitCheck = checkCreateProspectRateLimit();
    if (!rateLimitCheck.allowed) {
      setErrors({
        submit:
          rateLimitCheck.message ||
          "Too many signup attempts. Please try again later.",
      });
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token for bot protection (mandatory)
      let recaptchaToken = null;
      try {
        if (showChallenge) {
          // Checkbox mode - reset the invisible one first
          if (recaptchaScoreRef.current) {
            try {
              recaptchaScoreRef.current.reset();
            } catch (e) {
              // Ignore reset errors
            }
          }

          if (recaptchaChallengeRef.current) {
            recaptchaToken = recaptchaChallengeRef.current.getValue();

            if (!recaptchaToken) {
              setErrors({ submit: "Please complete the security check." });
              setLoading(false);
              return;
            }
          }
        } else {
          // Score mode (Invisible)
          if (recaptchaScoreRef.current) {
            recaptchaToken = await recaptchaScoreRef.current.executeAsync();
            recaptchaScoreRef.current.reset();
          }
        }
      } catch (recaptchaError) {
        logger.error("reCAPTCHA error:", recaptchaError);
        setLoading(false);
        setErrors({
          submit: "Security verification failed. Please try again.",
        });
        setErrors({
          submit: "Security verification failed. Please try again.",
        });
        return;
      }

      // Enforce reCAPTCHA token requirement
      if (!recaptchaToken) {
        setLoading(false);
        setErrors({
          submit: showChallenge
            ? "Please complete the security check."
            : "Security verification is required. Please enable JavaScript and try again.",
        });
        return;
      }


      const response = await fetch("/api/create-prospect", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          disabledOtp: true,
          with: "auth",
          recaptchaToken, // Include reCAPTCHA token for bot protection
          // Send the key we used so the backend validates against the correct one
          siteKey: showChallenge ? RECAPTCHA_CHECKBOX_KEY : RECAPTCHA_SCORE_KEY,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const destination = await completeSignup(result, selectedPlan);
        trackCompleteRegistration();
        setLoading(false);
        onClose();
        navigate(destination, { replace: true });
      } else {
        // Handle "requires_challenge" error
        if (result.error === "requires_challenge") {
          logger.warn("Low reCAPTCHA score, requiring visible challenge");
          setLoading(false);
          setShowChallenge(true); // Switch to visible mode
          setErrors({
            submit: "Please complete the security check below to continue.",
          });
          return;
        }

        setErrors({ submit: result.error || "Failed to create account" });
        setLoading(false);
      }
    } catch (error) {
      logger.error("Error creating prospect:", error);
      setErrors({ submit: "An error occurred. Please try again." });
      setLoading(false);
    }
  };

  return (
    <div className="overflow-y-auto max-h-[85vh]">
      <div className="space-y-4 px-2">
        <div className="text-center mb-3">
          <h2 className="text-xl font-bold text-brand-pink mb-1">
            Get Lexi&apos;s Tips Now
          </h2>
          <p className="text-xs text-gray-600">
            Create your account to get started
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label
              htmlFor="auth-modal-email"
              className="block text-[12px] font-bold text-gray-700 mb-1"
            >
              Email <sup className="text-xs text-red-500">*</sup>
            </label>
            <Input
              id="auth-modal-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: "" }));
              }}
              onBlur={() => {
                setErrors((prev) => {
                  if (!email) return { ...prev, email: "Email is required" };
                  if (!validateEmail(email))
                    return {
                      ...prev,
                      email: "Please enter a valid email address",
                    };
                  return prev;
                });
              }}
              className={cn("h-12", errors.email ? "border-red-500" : "")}
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Used for login and tip updates.
            </p>
            {errors.email && (
              <div className="mt-2">
                <ErrorMessage message={errors.email} type="field" />
              </div>
            )}
          </div>



          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 flex gap-2 items-center">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700 leading-snug">
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2 pt-3 border-t border-gray-200">
          <div>
            <h3 className="text-xs font-bold text-slate-900 mb-0.5">
              Choose Your Plan
            </h3>
            <p className="text-[10px] text-slate-600">
              Pick the option that works best for you
            </p>
          </div>

          {/* Plan Selection Cards */}
          <div className="space-y-1.5">
            {/* Free Plan */}
            <button
              type="button"
              onClick={() => setSelectedPlan("free")}
              disabled={loading}
              className={cn(
                "w-full px-2 py-3 border-2 rounded-md transition-all duration-200 text-left",
                "hover:border-pink-300 focus:outline-none focus:ring-1 focus:ring-pink-200",
                selectedPlan === "free"
                  ? "border-pink-500 bg-pink-50"
                  : "border-gray-200 bg-white",
                loading && "opacity-50 cursor-not-allowed",
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                      selectedPlan === "free"
                        ? "border-pink-500"
                        : "border-gray-300",
                    )}
                  >
                    {selectedPlan === "free" && (
                      <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-900 leading-tight">
                      Lexi Social
                    </p>
                    <p className="pt-2 text-[10px] text-slate-600 leading-tight">
                      2 Race-Day tips • All AU races • Full results history
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-pink-600">FREE</p>
                </div>
              </div>
            </button>

            {/* 7-Day Pass */}
            <button
              type="button"
              onClick={() => setSelectedPlan("day-pass")}
              disabled={loading}
              className={cn(
                "w-full px-2 py-3 border-2 rounded-md transition-all duration-200 text-left",
                "hover:border-pink-300 focus:outline-none focus:ring-1 focus:ring-pink-200",
                selectedPlan === "day-pass"
                  ? "border-pink-500 bg-pink-50"
                  : "border-gray-200 bg-white",
                loading && "opacity-50 cursor-not-allowed",
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                      selectedPlan === "day-pass"
                        ? "border-pink-500"
                        : "border-gray-300",
                    )}
                  >
                    {selectedPlan === "day-pass" && (
                      <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-900 leading-tight">
                      Lexi 7-Day Pass
                    </p>
                    <p className="pt-2 text-[10px] text-slate-600 leading-tight">
                      Lexi Pro full access pass for 7 days
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-pink-600">$9.00</p>
                  <p className="text-[10px] text-slate-500 leading-tight">
                    once-off
                  </p>
                </div>
              </div>
            </button>

            {/* Pro Plan */}
            <button
              type="button"
              onClick={() => setSelectedPlan("pro")}
              disabled={loading}
              className={cn(
                "w-full px-2 py-3 border-2 rounded-md transition-all duration-200 text-left relative",
                "hover:border-pink-300 focus:outline-none focus:ring-1 focus:ring-pink-200",
                selectedPlan === "pro"
                  ? "border-pink-500 bg-pink-50"
                  : "border-gray-200 bg-white",
                loading && "opacity-50 cursor-not-allowed",
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                      selectedPlan === "pro"
                        ? "border-pink-500"
                        : "border-gray-300",
                    )}
                  >
                    {selectedPlan === "pro" && (
                      <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-slate-900 leading-tight">
                        Lexi Pro
                      </p>
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-pink-600 text-white text-[9px] font-medium rounded-full">
                        <Check className="w-2 h-2" /> Popular
                      </span>
                    </div>
                    <p className="pt-2 text-[10px] text-slate-600 leading-tight">
                      4 Early Mail / Race-Day / Late Mail tips • Lexi Chat •
                      History
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-pink-600">$29.95</p>
                  <p className="text-[10px] text-slate-500 leading-tight">
                    /month
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <Button
          onClick={handleContactSubmit}
          disabled={loading}
          className="w-full h-10 bg-brand-pink hover:bg-[#e391c4] text-white font-semibold rounded-lg text-sm"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Redirecting to checkout…
            </>
          ) : (
            "Continue to secure checkout"
          )}
        </Button>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        <button
          onClick={() => {
            onClose();
            navigate("/login");
          }}
          className="w-full h-10 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
        >
          Sign In
        </button>

        {/* ReCAPTCHA Components */}

        {/* 1. Invisible Score-based (Primary) */}
        <ReCAPTCHA
          ref={recaptchaScoreRef}
          size="invisible"
          sitekey={RECAPTCHA_SCORE_KEY}
          badge="bottomright"
        />

        {/* 2. Visible Checkbox (Fallback) */}
        {showChallenge && (
          <div className="mt-4 flex justify-center pb-4">
            <ReCAPTCHA
              ref={recaptchaChallengeRef}
              sitekey={RECAPTCHA_CHECKBOX_KEY}
            />
          </div>
        )}

        <p className="text-[10px] text-gray-500 text-center leading-normal pt-1">
          This site is protected by reCAPTCHA and the <br />
          Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-pink hover:underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-pink hover:underline"
          >
            Terms of Service
          </a>{" "}
          apply.
        </p>
      </div>
    </div>
  );
}

export default function AuthModal({ isOpen, onClose, initialPlan = "pro" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle ESC key press
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Handle outside click
  const handleOutsideClick = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    },
    [onClose],
  );

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Render Sheet (drawer) on mobile, Modal on desktop
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          side="bottom"
          className="h-[100vh] xsm:h-[100vh] overflow-y-auto p-4 bg-white rounded-t-lg"
        >
          <div className="relative">
            <AuthContent
              email={email}
              setEmail={setEmail}
              loading={loading}
              setLoading={setLoading}
              onClose={onClose}
              initialPlan={initialPlan}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleOutsideClick}
    >
      <div
        ref={modalRef}
        className="bg-white overflow-hidden rounded-lg w-full max-w-md p-4 relative transform transition-all duration-300"
        style={{
          animation: "modalSlideIn 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
          data-gtm-element-id="auth-modal_close_button-1"
        >
          <X size={20} />
        </button>

        <AuthContent
          email={email}
          setEmail={setEmail}
          loading={loading}
          setLoading={setLoading}
          onClose={onClose}
          initialPlan={initialPlan}
        />
      </div>
    </div>
  );
}
